import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc,
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION_NAME = 'babyMovements';
const STATS_COLLECTION = 'babyMovementStats';

/**
 * Add a new baby movement entry
 * @param {Object} movementData - Movement data object
 * @returns {Promise<string>} Document ID
 */
export const addMovement = async (movementData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...movementData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update aggregate statistics
    await updateAggregateStats(movementData.userId, {
      rolls: movementData.rolls || 0,
      kicks: movementData.kicks || 0,
      count: 1
    }, 'add');
    
    // Update day-wise statistics
    await updateDayWiseStats(movementData.userId, movementData.dateIST, {
      rolls: movementData.rolls || 0,
      kicks: movementData.kicks || 0,
      count: 1
    }, 'add');
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding movement:', error);
    throw error;
  }
};

/**
 * Get a single movement by ID
 * @param {string} id - Document ID
 * @returns {Promise<Object>} Movement object
 */
export const getMovementById = async (id) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
        date: docSnap.data().date?.toDate()
      };
    } else {
      throw new Error('Movement not found');
    }
  } catch (error) {
    console.error('Error getting movement:', error);
    throw error;
  }
};

/**
 * Update an existing movement
 * @param {string} id - Document ID
 * @param {Object} data - Updated data
 * @returns {Promise<void>}
 */
export const updateMovement = async (id, data) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating movement:', error);
    throw error;
  }
};

/**
 * Delete a movement
 * @param {string} id - Document ID
 * @returns {Promise<void>}
 */
export const deleteMovement = async (id) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Movement not found');
    }
    
    const movementData = docSnap.data();
    
    await deleteDoc(docRef);
    
    // Update aggregate statistics
    await updateAggregateStats(movementData.userId, {
      rolls: movementData.rolls || 0,
      kicks: movementData.kicks || 0,
      count: 1
    }, 'subtract');
    
    // Update day-wise statistics
    await updateDayWiseStats(movementData.userId, movementData.dateIST, {
      rolls: movementData.rolls || 0,
      kicks: movementData.kicks || 0,
      count: 1
    }, 'subtract');
  } catch (error) {
    console.error('Error deleting movement:', error);
    throw error;
  }
};

/**
 * Get paginated movements for a specific user
 * @param {string} userId - User email
 * @param {number} pageSize - Number of items per page
 * @param {Object} lastDoc - Last document from previous page (null for first page)
 * @param {string} startDate - Optional start date filter (YYYY-MM-DD)
 * @param {string} endDate - Optional end date filter (YYYY-MM-DD)
 * @returns {Promise<Object>} Object containing movements array and lastVisible document
 */
export const getMovementsPaginated = async (userId, pageSize = 10, lastDoc = null, startDate = null, endDate = null) => {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(pageSize)
    );

    // Add pagination cursor if not first page
    if (lastDoc) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    let movements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate(), // Convert Firestore Timestamp to JS Date
      _doc: doc // Store document reference for pagination
    }));

    // Client-side date filtering if needed
    if (startDate) {
      movements = movements.filter(m => m.dateIST >= startDate);
    }
    if (endDate) {
      movements = movements.filter(m => m.dateIST <= endDate);
    }

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

    return {
      movements,
      lastVisible,
      hasMore: querySnapshot.docs.length === pageSize
    };
  } catch (error) {
    console.error('Error getting paginated movements:', error);
    
    // Check if it's an index error
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.error('Firestore index required. Please create an index for: userId (Ascending) + date (Descending)');
      console.error('Index URL should be provided in the error message above or in Firebase Console.');
    }
    
    throw error;
  }
};

/**
 * Get total count of movements for a user
 * @param {string} userId - User email
 * @param {string} startDate - Optional start date filter (YYYY-MM-DD)
 * @param {string} endDate - Optional end date filter (YYYY-MM-DD)
 * @returns {Promise<number>} Total count
 */
export const getMovementsCount = async (userId, startDate = null, endDate = null) => {
  try {
    // If we have date filters, we need to use paginated approach
    // This is still not optimal but better than fetching all data
    if (startDate || endDate) {
      // For now, return the count from stats and let the UI handle filtered display
      // A better approach would be to add date-based aggregation in the future
      const statsDocRef = doc(db, STATS_COLLECTION, userId);
      const statsSnap = await getDoc(statsDocRef);
      
      if (statsSnap.exists()) {
        return statsSnap.data().count || 0;
      }
      return 0;
    }

    const snapshot = await getCountFromServer(
      query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId)
      )
    );
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting movements count:', error);
    throw error;
  }
};

/**
 * Update aggregate statistics for a user
 * @param {string} userId - User email
 * @param {Object} values - Object with rolls, kicks, count
 * @param {string} operation - 'add' or 'subtract'
 * @returns {Promise<void>}
 */
const updateAggregateStats = async (userId, values, operation = 'add') => {
  try {
    const statsDocRef = doc(db, STATS_COLLECTION, userId);
    const statsSnap = await getDoc(statsDocRef);
    
    const multiplier = operation === 'add' ? 1 : -1;
    
    if (statsSnap.exists()) {
      const currentStats = statsSnap.data();
      await updateDoc(statsDocRef, {
        totalRolls: (currentStats.totalRolls || 0) + (values.rolls * multiplier),
        totalKicks: (currentStats.totalKicks || 0) + (values.kicks * multiplier),
        count: (currentStats.count || 0) + (values.count * multiplier),
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new stats document with userId as document ID
      await setDoc(statsDocRef, {
        userId,
        totalRolls: values.rolls,
        totalKicks: values.kicks,
        count: values.count,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating aggregate stats:', error);
    // Don't throw - allow the main operation to succeed even if stats update fails
  }
};

/**
 * Update day-wise statistics for a user
 * @param {string} userId - User email
 * @param {string} dateIST - Date in IST format (YYYY-MM-DD)
 * @param {Object} values - Object with rolls, kicks, count
 * @param {string} operation - 'add' or 'subtract'
 * @returns {Promise<void>}
 */
const updateDayWiseStats = async (userId, dateIST, values, operation = 'add') => {
  try {
    const statsDocRef = doc(db, STATS_COLLECTION, userId);
    const statsSnap = await getDoc(statsDocRef);
    
    const multiplier = operation === 'add' ? 1 : -1;
    
    let dayWiseData = {};
    
    if (statsSnap.exists()) {
      const currentStats = statsSnap.data();
      dayWiseData = currentStats.dayWise || {};
    }
    
    // Get or initialize the day's data
    const dayData = dayWiseData[dateIST] || { rolls: 0, kicks: 0, count: 0 };
    
    // Update the day's data
    dayWiseData[dateIST] = {
      rolls: Math.max(0, (dayData.rolls || 0) + (values.rolls * multiplier)),
      kicks: Math.max(0, (dayData.kicks || 0) + (values.kicks * multiplier)),
      count: Math.max(0, (dayData.count || 0) + (values.count * multiplier))
    };
    
    // Remove day entry if count becomes 0
    if (dayWiseData[dateIST].count === 0) {
      delete dayWiseData[dateIST];
    }
    
    if (statsSnap.exists()) {
      await updateDoc(statsDocRef, {
        dayWise: dayWiseData,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new stats document
      await setDoc(statsDocRef, {
        userId,
        totalRolls: values.rolls,
        totalKicks: values.kicks,
        count: values.count,
        dayWise: dayWiseData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating day-wise stats:', error);
    // Don't throw - allow the main operation to succeed even if stats update fails
  }
};

/**
 * Get aggregated totals (rolls and kicks) for a user
 * @param {string} userId - User email
 * @param {string} startDate - Optional start date filter (YYYY-MM-DD)
 * @param {string} endDate - Optional end date filter (YYYY-MM-DD)
 * @returns {Promise<Object>} Object with totalRolls, totalKicks, totalMovements
 */
export const getMovementsTotals = async (userId, startDate = null, endDate = null) => {
  try {
    // Note: Date filtering for totals is not supported in optimized mode
    // This would require fetching all records which defeats the optimization
    // For now, always return aggregate stats regardless of date filters
    if (startDate || endDate) {
      console.warn('Date filtering for totals requires fetching all records. Showing total stats instead.');
    }
    
    // Use aggregate stats for efficiency
    const statsDocRef = doc(db, STATS_COLLECTION, userId);
    const statsSnap = await getDoc(statsDocRef);
    
    if (statsSnap.exists()) {
      const stats = statsSnap.data();
      return {
        totalRolls: stats.totalRolls || 0,
        totalKicks: stats.totalKicks || 0,
        totalMovements: (stats.totalRolls || 0) + (stats.totalKicks || 0),
        count: stats.count || 0
      };
    }
    
    // No stats document exists yet - return zeros
    return {
      totalRolls: 0,
      totalKicks: 0,
      totalMovements: 0,
      count: 0
    };
  } catch (error) {
    console.error('Error getting movements totals:', error);
    throw error;
  }
};

/**
 * Get day-wise statistics for a user
 * @param {string} userId - User email
 * @returns {Promise<Object>} Object with dayWise data
 */
export const getDayWiseStats = async (userId) => {
  try {
    const statsDocRef = doc(db, STATS_COLLECTION, userId);
    const statsSnap = await getDoc(statsDocRef);
    
    if (statsSnap.exists()) {
      const stats = statsSnap.data();
      return stats.dayWise || {};
    }
    
    return {};
  } catch (error) {
    console.error('Error getting day-wise stats:', error);
    throw error;
  }
};

/**
 * Initialize or rebuild aggregate statistics for a user
 * This should be called if stats are missing or incorrect
 * Note: This fetches all records in batches - use sparingly
 * @param {string} userId - User email
 * @returns {Promise<void>}
 */
export const rebuildAggregateStats = async (userId) => {
  try {
    // Fetch all movements in one go for rebuild (this is a rare operation)
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const allMovements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate()
    }));
    
    const totalRolls = allMovements.reduce((sum, m) => sum + (m.rolls || 0), 0);
    const totalKicks = allMovements.reduce((sum, m) => sum + (m.kicks || 0), 0);
    
    // Build day-wise data
    const dayWiseData = {};
    allMovements.forEach(movement => {
      const dateIST = movement.dateIST;
      if (!dayWiseData[dateIST]) {
        dayWiseData[dateIST] = { rolls: 0, kicks: 0, count: 0 };
      }
      dayWiseData[dateIST].rolls += movement.rolls || 0;
      dayWiseData[dateIST].kicks += movement.kicks || 0;
      dayWiseData[dateIST].count += 1;
    });
    
    const statsDocRef = doc(db, STATS_COLLECTION, userId);
    
    const statsData = {
      userId,
      totalRolls,
      totalKicks,
      count: allMovements.length,
      dayWise: dayWiseData,
      updatedAt: serverTimestamp()
    };
    
    const statsSnap = await getDoc(statsDocRef);
    
    if (statsSnap.exists()) {
      await updateDoc(statsDocRef, statsData);
    } else {
      await setDoc(statsDocRef, {
        ...statsData,
        createdAt: serverTimestamp()
      });
    }
    
    return statsData;
  } catch (error) {
    console.error('Error rebuilding aggregate stats:', error);
    throw error;
  }
};

/**
 * Group movements by time sessions
 * @param {Array} movements - Array of movement objects
 * @param {number} sessionHours - Duration of each session in hours
 * @returns {Array} Array of grouped session objects
 */
export const groupMovementsBySession = (movements, sessionHours) => {
  if (!movements || movements.length === 0) return [];
  
  const sessionMilliseconds = sessionHours * 60 * 60 * 1000;
  const grouped = [];
  
  // Sort movements by date ascending
  const sorted = [...movements].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  let currentSession = {
    startTime: sorted[0].date,
    endTime: null,
    rolls: 0,
    kicks: 0,
    movements: []
  };
  
  sorted.forEach((movement, index) => {
    const movementTime = new Date(movement.date);
    const timeDiff = movementTime - new Date(currentSession.startTime);
    
    if (timeDiff <= sessionMilliseconds) {
      // Add to current session
      currentSession.rolls += movement.rolls || 0;
      currentSession.kicks += movement.kicks || 0;
      currentSession.movements.push(movement);
    } else {
      // Close current session and start new one
      currentSession.endTime = sorted[index - 1].date;
      grouped.push({...currentSession});
      
      currentSession = {
        startTime: movement.date,
        endTime: null,
        rolls: movement.rolls || 0,
        kicks: movement.kicks || 0,
        movements: [movement]
      };
    }
    
    // Handle last session
    if (index === sorted.length - 1) {
      currentSession.endTime = movement.date;
      grouped.push({...currentSession});
    }
  });
  
  return grouped;
};
